import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './Reviews.css';

// Reviews / social-proof page — ADDITIVE.
//
// LEGAL_REVIEW_REQUIRED + FTC/Google policy: the testimonials below are CLEARLY-LABELED ILLUSTRATIVE
// SAMPLES, not real audited customer reviews. They MUST be replaced with (or gated behind) genuine,
// verifiable reviews before this page is shown in production. Do NOT present sample content as real.
//
// Because the content is illustrative:
//  - No AggregateRating / Review JSON-LD is emitted (would be a structured-data policy violation to mark
//    up fake reviews). When real reviews exist, swap in genuine data AND add the schema then.
//  - No specific audited star count / total-review count is fabricated as fact; the on-page rating block
//    is explicitly labeled as an illustrative sample.
//
// SAMPLE flag: a single switch makes the gating obvious for the pre-prod review.
const IS_SAMPLE = true; // DEMO/SAMPLE - gate before prod

// {/* DEMO/SAMPLE - gate before prod */}
const SAMPLE_TESTIMONIALS = [
  { name: 'Sample customer', city: 'Chicago, IL', service: 'Cleaning', rating: 5, text: 'Example testimonial copy. Booking was quick and the pro was on time and thorough — placeholder content for layout only.' },
  { name: 'Sample customer', city: 'Houston, TX', service: 'Plumbing', rating: 5, text: 'Example testimonial copy. Fixed the leak fast and the price matched the upfront estimate — placeholder content for layout only.' },
  { name: 'Sample customer', city: 'New York, NY', service: 'TV Mounting', rating: 5, text: 'Example testimonial copy. Clean install, cables hidden, in and out in under an hour — placeholder content for layout only.' },
  { name: 'Sample customer', city: 'Phoenix, AZ', service: 'Moving Help', rating: 5, text: 'Example testimonial copy. Two pros handled the heavy lifting and nothing got scratched — placeholder content for layout only.' },
  { name: 'Sample customer', city: 'Dallas, TX', service: 'Electrical', rating: 4, text: 'Example testimonial copy. Replaced the fixtures and explained everything clearly — placeholder content for layout only.' },
  { name: 'Sample customer', city: 'Los Angeles, CA', service: 'Handyman', rating: 5, text: 'Example testimonial copy. Knocked out a whole to-do list in one visit — placeholder content for layout only.' },
];

const SERVICES = ['All', ...Array.from(new Set(SAMPLE_TESTIMONIALS.map((t) => t.service)))];

function Stars({ rating }) {
  return (
    <span className={s.stars} aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} viewBox="0 0 24 24" aria-hidden="true" className={n <= rating ? s.starOn : s.starOff}>
          <path d="m12 2 3 6.5 7 .9-5 4.8 1.2 7L12 18l-6.4 3.2L6.8 14l-5-4.8 7-.9L12 2Z" />
        </svg>
      ))}
    </span>
  );
}

function Reviews() {
  const [filter, setFilter] = React.useState('All');
  const list = filter === 'All' ? SAMPLE_TESTIMONIALS : SAMPLE_TESTIMONIALS.filter((t) => t.service === filter);

  return (
    <main className={s.page}>
      {/* LEGAL_REVIEW_REQUIRED: No AggregateRating/Review JSON-LD is emitted while content is illustrative
          (IS_SAMPLE). Add genuine review schema only when real, verifiable reviews back this page. */}

      <Hero
        eyebrow="Customer stories"
        title="Loved by people who hate to-do lists."
        subtitle="From quick fixes to full move-outs, customers come to BestHand for vetted local pros, upfront pricing and secure in-app payments."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="Browse all services"
        secondaryTo="/services"
      />

      {/* Sample disclosure banner — keeps the FTC-required labeling unmissable. */}
      {IS_SAMPLE && (
        // {/* DEMO/SAMPLE - gate before prod */}
        <div className={s.sampleBanner} role="note">
          Illustrative sample content — example reviews shown for layout. Real, verified customer reviews
          will appear here before launch.
        </div>
      )}

      {/* Aggregate rating block — labeled illustrative, NOT marked up as structured data. */}
      <section className={s.statsSec}>
        <div className={s.statsWrap}>
          <div className={s.statBig}>
            <div className={s.statNum}>4.9<span className={s.statSlash}>/5</span></div>
            <Stars rating={5} />
            {/* LEGAL_REVIEW_REQUIRED: figure is an illustrative sample, not an audited rating. */}
            <div className={s.statSub}>Illustrative average · sample</div>
          </div>
          <div className={s.statCol}>
            {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
            <div className={s.statItem}><span className={s.statK}>Vetted</span><span className={s.statV}>Background-checked pros</span></div>
            <div className={s.statItem}><span className={s.statK}>Upfront</span><span className={s.statV}>See your price before you book</span></div>
            <div className={s.statItem}><span className={s.statK}>Protected</span><span className={s.statV}>Secure in-app payments via Stripe</span></div>
          </div>
        </div>
      </section>

      {/* Filter chips by service */}
      <section className={s.filterSec}>
        <div className={s.wrap}>
          <div className={s.chips} role="group" aria-label="Filter reviews by service">
            {SERVICES.map((svc) => (
              <button
                type="button"
                key={svc}
                className={`${s.chip} ${filter === svc ? s.chipOn : ''}`}
                aria-pressed={filter === svc}
                onClick={() => setFilter(svc)}
              >
                {svc}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Wall of (sample) testimonials */}
      <section className={s.wallSec}>
        <div className={s.wrap}>
          <div className={s.wall}>
            {list.map((t, i) => (
              // {/* DEMO/SAMPLE - gate before prod */}
              <figure className={s.review} key={`${t.service}-${i}`}>
                <Stars rating={t.rating} />
                <blockquote className={s.quote}>“{t.text}”</blockquote>
                <figcaption className={s.meta}>
                  <span className={s.metaName}>{t.name}</span>
                  <span className={s.metaSub}>{t.service} · {t.city}</span>
                </figcaption>
                <span className={s.sampleTag}>Sample</span>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Leave a review */}
      <section className={`${s.leaveSec}`}>
        <div className={s.wrap}>
          <div className={s.leaveCard}>
            <div>
              <h2 className={s.leaveTitle}>Worked with a BestHand pro?</h2>
              <p className={s.leaveText}>Your feedback helps other customers book with confidence — and helps great pros stand out.</p>
            </div>
            <Link to="/leave-a-review" className={s.leaveBtn}>Leave a review</Link>
          </div>
        </div>
      </section>

      <CtaBanner
        title="See why customers keep coming back."
        subtitle="Book a vetted local pro with upfront, industry-low pricing."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="How it works"
        secondaryTo="/how-it-works"
      />
    </main>
  );
}

export default withStyles(s)(Reviews);
